/**
 * EmotionalAnchors - Reassuring & Motivating Messages
 * Creates emotional connection with users through human language
 * Part of the User Love Layer
 */
import React from 'react';
import { motion } from 'framer-motion';
import { 
  Heart, Sparkles, Coffee, Sun, Moon, Cloud, 
  Shield, CheckCircle, Clock, Zap, Users, Star
} from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Welcome Message - First impression
 */
export const WelcomeMessage = ({ 
  isReturning = false, 
  language = 'en',
  userName,
  className 
}) => {
  const messages = {
    new: {
      en: { title: "Find help fast.", subtitle: "No hassle." },
      et: { title: "Leia abi kiirelt.", subtitle: "Vaevata." },
      fi: { title: "Löydä apua nopeasti.", subtitle: "Vaivatta." },
      nb: { title: "Finn hjelp raskt.", subtitle: "Uten stress." },
      ru: { title: "Найдите помощь быстро.", subtitle: "Без хлопот." }
    },
    returning: {
      en: { title: `Welcome back${userName ? `, ${userName}` : ''}!`, subtitle: "Ready when you are." },
      et: { title: `Tere tulemast tagasi${userName ? `, ${userName}` : ''}!`, subtitle: "Valmis, kui sina oled." },
      fi: { title: `Tervetuloa takaisin${userName ? `, ${userName}` : ''}!`, subtitle: "Valmis kun olet." },
      nb: { title: `Velkommen tilbake${userName ? `, ${userName}` : ''}!`, subtitle: "Klar når du er." },
      ru: { title: `С возвращением${userName ? `, ${userName}` : ''}!`, subtitle: "Готовы, когда вы готовы." }
    }
  };

  const msg = isReturning ? messages.returning : messages.new;
  const content = msg[language] || msg.en;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn("text-center", className)}
    >
      <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">
        {content.title}
      </h1>
      <p className="text-lg text-slate-600">{content.subtitle}</p>
    </motion.div>
  );
};

/**
 * Time-based Greeting
 */
export const TimeGreeting = ({ language = 'en', className }) => {
  const hour = new Date().getHours();
  
  const getGreeting = () => {
    if (hour >= 5 && hour < 12) {
      return {
        icon: Sun,
        color: "text-yellow-500",
        message: {
          en: "Good morning! What can we help with today?",
          et: "Tere hommikust! Kuidas saame täna aidata?",
          fi: "Hyvää huomenta! Kuinka voimme auttaa tänään?"
        }
      };
    }
    if (hour >= 12 && hour < 17) {
      return {
        icon: Cloud,
        color: "text-blue-500",
        message: {
          en: "Good afternoon! Looking for something specific?",
          et: "Tere päevast! Otsid midagi konkreetset?",
          fi: "Hyvää iltapäivää! Etsitkö jotain erityistä?"
        }
      };
    }
    if (hour >= 17 && hour < 21) {
      return {
        icon: Coffee,
        color: "text-orange-500",
        message: {
          en: "Good evening! Let's get things done.",
          et: "Tere õhtust! Teeme asjad ära.",
          fi: "Hyvää iltaa! Hoidetaan asiat kuntoon."
        }
      };
    }
    return {
      icon: Moon,
      color: "text-purple-500",
      message: {
        en: "Working late? We're here to help.",
        et: "Töötad hilja? Oleme siin, et aidata.",
        fi: "Töissä myöhään? Olemme täällä auttamassa."
      }
    };
  };

  const greeting = getGreeting();
  const Icon = greeting.icon;
  const message = greeting.message[language] || greeting.message.en;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className={cn("flex items-center gap-2 text-slate-600", className)}
    >
      <Icon className={cn("h-5 w-5", greeting.color)} />
      <span>{message}</span>
    </motion.div>
  );
};

/**
 * Reassurance Message - After actions
 */
export const ReassuranceMessage = ({ 
  type = 'submitted', // submitted, waiting, matched, confirmed
  language = 'en',
  className 
}) => {
  const messages = {
    submitted: {
      icon: CheckCircle,
      color: "text-green-500",
      bg: "bg-green-50",
      content: {
        en: { title: "You're done.", subtitle: "We'll handle the rest." },
        et: { title: "Oled valmis.", subtitle: "Meie hoolitseme ülejäänu eest." },
        fi: { title: "Olet valmis.", subtitle: "Hoidamme loput." },
        nb: { title: "Du er ferdig.", subtitle: "Vi tar oss av resten." },
        ru: { title: "Готово!", subtitle: "Остальное — наша забота." }
      }
    },
    waiting: {
      icon: Clock,
      color: "text-blue-500",
      bg: "bg-blue-50",
      content: {
        en: { title: "Sit back and relax.", subtitle: "Providers are being notified." },
        et: { title: "Istu ja lõdvestu.", subtitle: "Tegijaid teavitatakse." },
        fi: { title: "Istu ja rentoudu.", subtitle: "Tarjoajia ilmoitetaan." },
        nb: { title: "Len deg tilbake.", subtitle: "Leverandører blir varslet." },
        ru: { title: "Расслабьтесь.", subtitle: "Уведомляем исполнителей." }
      }
    },
    matched: {
      icon: Sparkles,
      color: "text-purple-500",
      bg: "bg-purple-50",
      content: {
        en: { title: "Great news!", subtitle: "We found providers who match your needs." },
        et: { title: "Suurepärased uudised!", subtitle: "Leidsime sinu vajadustele sobivad tegijad." },
        fi: { title: "Hyviä uutisia!", subtitle: "Löysimme tarpeisiisi sopivat tarjoajat." },
        nb: { title: "Gode nyheter!", subtitle: "Vi fant leverandører som matcher behovet ditt." },
        ru: { title: "Отличные новости!", subtitle: "Мы нашли исполнителей под ваш запрос." }
      }
    },
    confirmed: {
      icon: Heart,
      color: "text-pink-500",
      bg: "bg-pink-50",
      content: {
        en: { title: "All set!", subtitle: "Your booking is confirmed." },
        et: { title: "Kõik korras!", subtitle: "Sinu broneering on kinnitatud." },
        fi: { title: "Kaikki kunnossa!", subtitle: "Varauksesi on vahvistettu." },
        nb: { title: "Alt er klart!", subtitle: "Bestillingen din er bekreftet." },
        ru: { title: "Всё готово!", subtitle: "Ваше бронирование подтверждено." }
      }
    }
  };

  const config = messages[type] || messages.submitted;
  const Icon = config.icon;
  const content = config.content[language] || config.content.en;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className={cn(
        "p-6 rounded-xl text-center",
        config.bg,
        className
      )}
    >
      <div className={cn(
        "w-12 h-12 mx-auto mb-4 rounded-full flex items-center justify-center",
        config.bg.replace('50', '100')
      )}>
        <Icon className={cn("h-6 w-6", config.color)} />
      </div>
      <h3 className="text-xl font-bold text-slate-900 mb-1">{content.title}</h3>
      <p className="text-slate-600">{content.subtitle}</p>
    </motion.div>
  );
};

/**
 * Motivational Tip
 */
export const MotivationalTip = ({ 
  language = 'en',
  context = 'booking', // booking, waiting, browsing
  className 
}) => {
  const tips = {
    booking: {
      en: [
        "Tip: More details = better matches",
        "Pro tip: Add photos for faster quotes",
        "Hint: Flexible timing gets more responses"
      ],
      et: [
        "Vihje: Rohkem detaile = paremad vasted",
        "Pro vihje: Lisa pilte kiiremate pakkumiste jaoks",
        "Vihje: Paindlik aeg saab rohkem vastuseid"
      ],
      fi: [
        "Vinkki: Enemmän tietoja = paremmat osumat",
        "Pro vinkki: Lisää kuvia nopeampiin tarjouksiin",
        "Vinkki: Joustava aika saa enemmän vastauksia"
      ]
    },
    waiting: {
      en: [
        "Most responses arrive within 2 hours",
        "Quick Response users get replies in 10-15 min",
        "We'll email you when someone responds"
      ],
      et: [
        "Enamik vastuseid saabub 2 tunni jooksul",
        "Kiirvastsuse kasutajad saavad vastused 10-15 min jooksul",
        "Saadame sulle meili, kui keegi vastab"
      ],
      fi: [
        "Useimmat vastaukset tulevat 2 tunnin sisällä",
        "Pikavastaus-käyttäjät saavat vastaukset 10-15 min sisällä",
        "Lähetämme sinulle sähköpostia kun joku vastaa"
      ]
    },
    browsing: {
      en: [
        "Save time: bookmark jobs you like",
        "Set up alerts to never miss a match",
        "Verified providers respond faster"
      ],
      et: [
        "Säästä aega: lisa meeldivad tööd järjehoidjasse",
        "Seadista teavitused, et kunagi ei jääks midagi märkamata",
        "Kinnitatud tegijad vastavad kiiremini"
      ],
      fi: [
        "Säästä aikaa: tallenna työt kirjanmerkkeihin",
        "Aseta hälytyksiä, ettet koskaan jää paitsi",
        "Vahvistetut tarjoajat vastaavat nopeammin"
      ]
    }
  };

  const contextTips = tips[context] || tips.booking;
  const langTips = contextTips[language] || contextTips.en;
  const randomTip = langTips[Math.floor(Math.random() * langTips.length)];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.5 }}
      className={cn(
        "flex items-center gap-2 text-sm text-slate-500 italic",
        className
      )}
    >
      <Sparkles className="h-4 w-4 text-yellow-500" />
      <span>{randomTip}</span>
    </motion.div>
  );
};

/**
 * Social Proof Banner
 */
export const SocialProofBanner = ({ 
  language = 'en',
  stats = { providers: 500, requests: 10000, countries: 27 },
  className 
}) => {
  const labels = {
    en: { providers: "providers", requests: "requests", countries: "countries" },
    et: { providers: "tegijat", requests: "päringut", countries: "riiki" },
    fi: { providers: "tarjoajaa", requests: "pyyntöä", countries: "maata" }
  };

  const l = labels[language] || labels.en;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        "flex flex-wrap justify-center gap-6 text-sm text-slate-500",
        className
      )}
    >
      <div className="flex items-center gap-1.5">
        <Users className="h-4 w-4 text-orange-500" />
        <span><strong className="text-slate-700">{stats.providers}+</strong> {l.providers}</span>
      </div>
      <div className="flex items-center gap-1.5">
        <Zap className="h-4 w-4 text-yellow-500" />
        <span><strong className="text-slate-700">{stats.requests.toLocaleString()}+</strong> {l.requests}</span>
      </div>
      <div className="flex items-center gap-1.5">
        <Shield className="h-4 w-4 text-green-500" />
        <span><strong className="text-slate-700">{stats.countries}+</strong> {l.countries}</span>
      </div>
    </motion.div>
  );
};

/**
 * Loading State with Personality
 */
export const FriendlyLoader = ({ 
  language = 'en',
  context = 'loading', // loading, matching, sending
  className 
}) => {
  const messages = {
    loading: {
      en: ["Hold on...", "Almost there...", "Just a moment..."],
      et: ["Oota veidi...", "Peaaegu valmis...", "Hetk veel..."],
      fi: ["Odota hetki...", "Melkein valmis...", "Hetken vielä..."]
    },
    matching: {
      en: ["Finding the best matches...", "AI is working...", "Scanning providers..."],
      et: ["Leiame parimad vasted...", "AI töötab...", "Otsime tegijaid..."],
      fi: ["Etsitään parhaat...", "AI työskentelee...", "Haetaan tarjoajia..."]
    },
    sending: {
      en: ["Sending your request...", "Almost done...", "Notifying providers..."],
      et: ["Saadame päringut...", "Peaaegu valmis...", "Teavitame tegijaid..."],
      fi: ["Lähetetään pyyntöä...", "Melkein valmis...", "Ilmoitetaan tarjoajille..."]
    }
  };

  const contextMessages = messages[context] || messages.loading;
  const langMessages = contextMessages[language] || contextMessages.en;
  const [messageIndex, setMessageIndex] = React.useState(0);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex(prev => (prev + 1) % langMessages.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [langMessages.length]);

  return (
    <motion.div
      className={cn("text-center py-8", className)}
    >
      <div className="relative w-12 h-12 mx-auto mb-4">
        <motion.div
          className="absolute inset-0 border-4 border-orange-200 rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        />
        <motion.div
          className="absolute inset-0 border-4 border-transparent border-t-orange-500 rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        />
      </div>
      <motion.p
        key={messageIndex}
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-slate-600"
      >
        {langMessages[messageIndex]}
      </motion.p>
    </motion.div>
  );
};

/**
 * Empty State with Encouragement
 */
export const EmptyStateEncouragement = ({ 
  type = 'no_results', // no_results, no_bookings, no_responses
  language = 'en',
  className 
}) => {
  const content = {
    no_results: {
      icon: Star,
      color: "text-yellow-500",
      bg: "bg-yellow-50",
      message: {
        en: { title: "No results yet", subtitle: "Try adjusting your search or check back soon" },
        et: { title: "Tulemusi pole veel", subtitle: "Proovi otsingut muuta või vaata hiljem" },
        fi: { title: "Ei tuloksia vielä", subtitle: "Kokeile muuttaa hakua tai tarkista myöhemmin" }
      }
    },
    no_bookings: {
      icon: Sparkles,
      color: "text-purple-500",
      bg: "bg-purple-50",
      message: {
        en: { title: "No bookings yet", subtitle: "Your first booking is just a click away" },
        et: { title: "Broneeringuid pole veel", subtitle: "Esimene broneering on vaid kliki kaugusel" },
        fi: { title: "Ei varauksia vielä", subtitle: "Ensimmäinen varaus on vain klikkauksen päässä" }
      }
    },
    no_responses: {
      icon: Clock,
      color: "text-blue-500",
      bg: "bg-blue-50",
      message: {
        en: { title: "Waiting for responses", subtitle: "Providers usually respond within 24 hours" },
        et: { title: "Ootame vastuseid", subtitle: "Tegijad vastavad tavaliselt 24 tunni jooksul" },
        fi: { title: "Odotetaan vastauksia", subtitle: "Tarjoajat vastaavat yleensä 24 tunnin sisällä" }
      }
    }
  };

  const config = content[type] || content.no_results;
  const Icon = config.icon;
  const msg = config.message[language] || config.message.en;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className={cn(
        "text-center py-12 px-6 rounded-xl",
        config.bg,
        className
      )}
    >
      <div className={cn(
        "w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center bg-white"
      )}>
        <Icon className={cn("h-8 w-8", config.color)} />
      </div>
      <h3 className="text-lg font-semibold text-slate-900 mb-1">{msg.title}</h3>
      <p className="text-slate-600">{msg.subtitle}</p>
    </motion.div>
  );
};

export default {
  WelcomeMessage,
  TimeGreeting,
  ReassuranceMessage,
  MotivationalTip,
  SocialProofBanner,
  FriendlyLoader,
  EmptyStateEncouragement
};
